import {useMemo} from 'react';

/**
 * @type {WeakMap<any, number>}
 */
const uniqueIdMap = new WeakMap();

/**
 * Creates a new id for a given object.
 *
 * @param {unknown} object Object reference to create an id for.
 * @return {number}
 */
export function createId(object: any) {
  const instances = uniqueIdMap.get(object) || 0;
  uniqueIdMap.set(object, instances + 1);

  return instances;
}

/**
 * A custom hook to create a unique `id` for components. This is useful for binding form elements together to support accessibility devices.
 *
 * ### React 18 implementation
 *
 * [React 18](https://reactjs.org/blog/2021/06/08/the-plan-for-react-18.html) is planning on shipping with a SSR friendly id generation, [useOpaqueIdentifier](https://github.com/facebook/react/pull/17322). So in the future whenever React 18 comes out, and we migrate to it, we should switch over.
 *
 * ## Usage
 *
 * In the example (below), everytime a new instance of the `Example` component is mounted and rendered, a new unique `id` will be created for that instance. Re-rendering of components does not cause `useUniqueId` to create a new `id`.
 *
 * @example
 *
 * ```jsx
 * import React from 'react';
 * import useUniqueId from '@packages/systems/design-system/useUniqueId';
 *
 * function Example() {
 *   const id = useUniqueId(Example, 'example'); // example-0
 *
 *   return <div id={id}>Example</div>;
 * }
 * ```
 *
 * ### Preferred id
 *
 * To consolidate (and prefer) incoming `id` values, provide the incoming `id` prop as the 3rd argument for `useUniqueId`.
 *
 * @example
 *
 * ```jsx
 * import React from 'react';
 * import useUniqueId from '@packages/systems/design-system/useUniqueId';
 *
 * function Example({id: idProp = 'hello'}) {
 *   const id = useUniqueId(Example, 'example', idProp); // hello
 *
 *   return <div id={id}>Example</div>;
 * }
 * ```
 */
export function useUniqueId(
  object: any,
  prefix: string,
  preferredId: string = ''
): string {
  return useMemo(() => {
    if (preferredId) return preferredId;
    const id = createId(object);

    return prefix ? `${prefix}-${id}` : id.toString();
  }, [object, preferredId, prefix]);
}

export default useUniqueId;
